import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Sparkle, Copy, Calendar, Lightning, Package, 
  Browser, Envelope, TrendUp, VideoCamera, ChartLineUp, PenNib
} from '@phosphor-icons/react';
import { useWorkflowStore } from '@/store/workflowStore';

// What's Next suggestion card
const SuggestionCard = ({ icon: Icon, title, description, onClick, accentColor = 'blue', badge }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600',
    emerald: 'from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
    amber: 'from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    purple: 'from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
    green: 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
  };

  const badgeColors = {
    recommended: 'bg-green-500/20 text-green-400',
    popular: 'bg-amber-500/20 text-amber-400',
    new: 'bg-purple-500/20 text-purple-400'
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 text-left transition-all group"
    >
      <div className="flex items-start space-x-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[accentColor]} flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className="text-white" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h4 className="text-white font-medium">{title}</h4>
            {badge && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${badgeColors[badge]}`}>
                {badge === 'recommended' ? 'Recommended' : badge === 'popular' ? 'Popular' : 'New'}
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm mt-0.5">{description}</p>
        </div>
        <ArrowRight size={20} className="text-slate-500 group-hover:text-white transition-colors flex-shrink-0" />
      </div>
    </motion.button>
  );
};

// What's Next section for after content generation
export const WhatsNextContent = ({ onAction, onClose }) => {
  const suggestions = [
    {
      id: 'run-my-week',
      icon: Calendar,
      title: 'Plan Your Week',
      description: 'Get a 7-day content calendar personalized for you',
      accentColor: 'blue',
      badge: 'recommended',
      action: 'run-my-week'
    },
    {
      id: 'clone-viral',
      icon: Copy,
      title: 'Clone Another Viral Post',
      description: 'Find and adapt more winning content',
      accentColor: 'emerald',
      action: 'clone-viral'
    },
    {
      id: 'improve-hook',
      icon: PenNib,
      title: 'Improve Your Hook',
      description: 'Make your opening even more attention-grabbing',
      accentColor: 'purple',
      badge: 'new',
      action: 'improve-hook'
    },
    {
      id: 'score-content',
      icon: ChartLineUp,
      title: 'Score My Content',
      description: 'Get AI feedback on your post before publishing',
      accentColor: 'amber',
      action: 'score-content'
    }
  ];

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center space-x-2">
        <Lightning size={18} className="text-amber-400" weight="fill" />
        <h3 className="text-white font-semibold">What's Next?</h3>
      </div>
      <div className="grid gap-3">
        {suggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            icon={suggestion.icon}
            title={suggestion.title}
            description={suggestion.description}
            accentColor={suggestion.accentColor}
            badge={suggestion.badge}
            onClick={() => {
              if (onAction) onAction(suggestion.action);
              if (onClose) onClose();
            }}
          />
        ))}
      </div>
    </div>
  );
};

// What's Next section for after monetization tools
export const WhatsNextMonetization = ({ toolUsed, onAction, onClose, workflowState = {} }) => {
  // Get workflow state from Zustand store as primary source
  // Select individual values to avoid creating new object reference on each render
  const offerCreated = useWorkflowStore((state) => state.offerCreated);
  const salesPageCreated = useWorkflowStore((state) => state.salesPageCreated);
  const emailCampaignCreated = useWorkflowStore((state) => state.emailCampaignCreated);
  const offerName = useWorkflowStore((state) => state.offerName);
  
  // Use store state as primary, fall back to props for backward compatibility
  const {
    hasOffer = offerCreated || false,
    hasSalesPage = salesPageCreated || false,
    hasEmailCampaign = emailCampaignCreated || false,
    hasMonetizationPlan = false
  } = workflowState;

  const getSuggestions = () => {
    const suggestions = [];

    // After email campaign is created, prioritize forward-looking actions
    if (toolUsed === 'email-campaign' || (hasOffer && hasSalesPage && hasEmailCampaign)) {
      // User has completed the main monetization flow
      // Recommend promotion and growth actions
      suggestions.push({
        id: 'create-content',
        icon: Sparkle,
        title: 'Create Content to Promote',
        description: 'Generate viral content to promote your offer',
        accentColor: 'emerald',
        badge: 'recommended',
        action: 'generate-content'
      });

      suggestions.push({
        id: 'weekly-plan',
        icon: Calendar,
        title: 'Plan Your Launch Week',
        description: 'Schedule 7 days of promotional content',
        accentColor: 'blue',
        action: 'run-my-week'
      });

      if (!hasMonetizationPlan) {
        suggestions.push({
          id: 'monetization-plan',
          icon: TrendUp,
          title: 'Get Your Income Roadmap',
          description: 'Personalized path to your income goal',
          accentColor: 'green',
          action: 'monetization-plan'
        });
      }

      suggestions.push({
        id: 'generate-video',
        icon: VideoCamera,
        title: 'Generate Video Content',
        description: 'Turn your script into a video',
        accentColor: 'purple',
        action: 'generate-video'
      });

      return suggestions.slice(0, 4);
    }

    // After sales page, recommend email campaign
    if (toolUsed === 'sales-page' || (hasOffer && hasSalesPage && !hasEmailCampaign)) {
      suggestions.push({
        id: 'email-campaign',
        icon: Envelope,
        title: 'Write Email Campaign',
        description: 'Create email sequences to launch your offer',
        accentColor: 'blue',
        badge: 'recommended',
        action: 'email-campaign'
      });

      suggestions.push({
        id: 'create-content',
        icon: Sparkle,
        title: 'Create Content to Promote',
        description: 'Generate viral content about your offer',
        accentColor: 'emerald',
        action: 'generate-content'
      });

      if (!hasMonetizationPlan) {
        suggestions.push({
          id: 'monetization-plan',
          icon: TrendUp,
          title: 'Get Your Income Roadmap',
          description: 'Personalized path to your income goal',
          accentColor: 'green',
          action: 'monetization-plan'
        });
      }

      return suggestions.slice(0, 4);
    }

    // After offer, recommend sales page
    if (toolUsed === 'build-offer' || (hasOffer && !hasSalesPage)) {
      suggestions.push({
        id: 'sales-page',
        icon: Browser,
        title: 'Create Sales Page',
        description: 'Generate high-converting sales copy',
        accentColor: 'purple',
        badge: 'recommended',
        action: 'sales-page'
      });

      if (!hasEmailCampaign) {
        suggestions.push({
          id: 'email-campaign',
          icon: Envelope,
          title: 'Write Email Campaign',
          description: 'Create email sequences that sell',
          accentColor: 'blue',
          action: 'email-campaign'
        });
      }

      if (!hasMonetizationPlan) {
        suggestions.push({
          id: 'monetization-plan',
          icon: TrendUp,
          title: 'Get Your Income Roadmap',
          description: 'Personalized path to your income goal',
          accentColor: 'green',
          action: 'monetization-plan'
        });
      }

      return suggestions.slice(0, 4);
    }

    // After monetization plan or no specific tool, show logical next steps
    if (toolUsed === 'monetization-plan' || !toolUsed) {
      if (!hasOffer) {
        suggestions.push({
          id: 'build-offer',
          icon: Package,
          title: 'Build Your Offer',
          description: 'Create a digital product to sell',
          accentColor: 'amber',
          badge: 'recommended',
          action: 'build-offer'
        });
      }

      if (hasOffer && !hasSalesPage) {
        suggestions.push({
          id: 'sales-page',
          icon: Browser,
          title: 'Create Sales Page',
          description: 'Generate high-converting sales copy',
          accentColor: 'purple',
          badge: hasOffer ? 'recommended' : undefined,
          action: 'sales-page'
        });
      }

      if (hasOffer && hasSalesPage && !hasEmailCampaign) {
        suggestions.push({
          id: 'email-campaign',
          icon: Envelope,
          title: 'Write Email Campaign',
          description: 'Create email sequences that sell',
          accentColor: 'blue',
          badge: 'recommended',
          action: 'email-campaign'
        });
      }

      suggestions.push({
        id: 'create-content',
        icon: Sparkle,
        title: 'Create Content',
        description: 'Generate viral content for your audience',
        accentColor: 'emerald',
        action: 'generate-content'
      });

      return suggestions.slice(0, 4);
    }

    // Fallback - show all remaining steps in order
    if (!hasOffer) {
      suggestions.push({
        id: 'build-offer',
        icon: Package,
        title: 'Build Your Offer',
        description: 'Create a digital product to sell',
        accentColor: 'amber',
        action: 'build-offer'
      });
    }

    if (!hasSalesPage) {
      suggestions.push({
        id: 'sales-page',
        icon: Browser,
        title: 'Create Sales Page',
        description: 'Generate high-converting sales copy',
        accentColor: 'purple',
        action: 'sales-page'
      });
    }

    if (!hasEmailCampaign) {
      suggestions.push({
        id: 'email-campaign',
        icon: Envelope,
        title: 'Write Email Campaign',
        description: 'Create email sequences that sell',
        accentColor: 'blue',
        action: 'email-campaign'
      });
    }

    suggestions.push({
      id: 'create-content',
      icon: Sparkle,
      title: 'Create Content to Promote',
      description: 'Generate viral content about your offer',
      accentColor: 'emerald',
      action: 'generate-content'
    });

    return suggestions.slice(0, 4);
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center space-x-2">
        <Lightning size={18} className="text-amber-400" weight="fill" />
        <h3 className="text-white font-semibold">What's Next?</h3>
      </div>
      <div className="grid gap-3">
        {getSuggestions().map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            icon={suggestion.icon}
            title={suggestion.title}
            description={suggestion.description}
            accentColor={suggestion.accentColor}
            badge={suggestion.badge}
            onClick={() => {
              if (onAction) onAction(suggestion.action);
              if (onClose) onClose();
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Compact inline suggestion (for use within modals)
export const InlineSuggestion = ({ icon: Icon, text, onClick, accentColor = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400 hover:text-blue-300',
    emerald: 'text-emerald-400 hover:text-emerald-300',
    amber: 'text-amber-400 hover:text-amber-300',
    purple: 'text-purple-400 hover:text-purple-300'
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 ${colorClasses[accentColor]} text-sm transition-colors`}
    >
      <Icon size={16} weight="fill" />
      <span>{text}</span>
      <ArrowRight size={14} />
    </button>
  );
};

export default {
  WhatsNextContent,
  WhatsNextMonetization,
  InlineSuggestion,
  SuggestionCard
};
